import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary, FormControl, FormControlLabel, Switch, TextField } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import { Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AppBar from '@material-ui/core/AppBar';
import { BackgroundType, BACKGROUND_MODE_OVERRIDE, getBackgroundTypeName, useSettings } from '../../contexts/SettingsProvider';
import { CustomThemeParams, themeMap } from '../../themes/theme'
import ThemeItem from './ThemeItem';
import CustomThemeBuilder from './CustomThemeBuilder';
import CustomCardBuilder from './CustomCardBuilder';
import { isStringNumber } from '../../util/util';
import BackgroundItem from './BackgroundItem';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
    },
    title: {
        flexGrow: 1,
    },
    body: {
        padding: theme.spacing(2, 2, 3),
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 1.5, 2),
        },
    },
    details: {
        display: 'block',
        padding: theme.spacing(1, 2, 2),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 1, 1.5),
        },
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
        gap: 12,
        width: '100%',
        marginBottom: 8,
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 8,
        },
    },
    general: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 360,
    },
}))

interface ISettingsProps {
    handleCloseSettings: () => void
}

export default function Settings({ handleCloseSettings }: ISettingsProps) {
    const classes = useStyles()
    const { theme, setTheme, getCustomTheme, customThemeParams, setCustomThemeParams, name, setName,
        autoUseName, setAutoUseName, shouldSavePreviousRoomId, setShouldSavePreviousRoomId,
        customCardColor, setCustomCardColor, useCustomCardColor, setUseCustomCardColor,
        cardOutlineColor, setCardOutlineColor, useCustomCardOutline, setUseCustomCardOutline,
        cardOutlineThickness, setCardOutlineThickness,
        cardImage, setCardImage, customCardImage, setCustomCardImage,
        backgroundType, setBackgroundType } = useSettings()

    const [editCustomThemeParams, setEditCustomThemeParams] = useState(customThemeParams)

    function handleThemeSelected(themeKey: string) {
        setTheme(themeKey)
    }

    function handleNameTextChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setName(e.target.value)
    }

    function handleAutoUseNameChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setAutoUseName(e.target.checked)
    }

    function handleSavePreviousRoomIdChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setShouldSavePreviousRoomId(e.target.checked)
    }

    function handleCustomThemeEdited(newParams: CustomThemeParams) {
        setEditCustomThemeParams(newParams)
    }

    function handleCustomThemeUpdatePressed() {
        setCustomThemeParams(editCustomThemeParams)
    }

    function renderThemesList() {
        var useThemes = Array.from(themeMap.entries())
        useThemes.push(['custom', { theme: getCustomTheme(editCustomThemeParams), name: 'Custom' }])
        return useThemes.map(list =>
            <ThemeItem key={`theme-item-${list[0]}`} themeKey={list[0]} theme={list[1]} isSelected={theme == list[0]} onSelected={handleThemeSelected} />
        )
    }

    function renderBackgroundList() {
        return Object.keys(BackgroundType).filter(value => !isStringNumber(value)).map(type =>
            <BackgroundItem key={`background-item-${type}`} type={BackgroundType[type as keyof typeof BackgroundType]} isSelected={BackgroundType[type as keyof typeof BackgroundType] === backgroundType} onSelected={(newType: BackgroundType) => {
                setBackgroundType(newType)
            }} />
        )
    }

    return (
        <div className={classes.root}>
            <AppBar position="static">
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleCloseSettings} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        Settings
                    </Typography>
                </Toolbar>
            </AppBar>

            <div className={classes.body}>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">

                        <Typography variant="h6">General</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                        <FormControl component="fieldset" className={classes.general}>
                            <TextField label="Name"
                                onChange={handleNameTextChanged}
                                defaultValue={name}
                                inputProps={{ maxLength: 30 }}
                                style={{ marginBottom: '16px' }} />
                            <FormControlLabel
                                control={<Switch
                                    checked={autoUseName}
                                    onChange={handleAutoUseNameChanged}
                                    color="primary" />}
                                label="Automatically use name when joining rooms" />

                            <FormControlLabel
                                control={<Switch
                                    checked={shouldSavePreviousRoomId}
                                    onChange={handleSavePreviousRoomIdChanged}
                                    color="primary" />}
                                label="Save previous room Id" />
                        </FormControl>
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">

                        <Typography variant="h6">Theme</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                        {BACKGROUND_MODE_OVERRIDE[backgroundType] ?
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
                                {getBackgroundTypeName(backgroundType)} forces {BACKGROUND_MODE_OVERRIDE[backgroundType]} mode.
                            </Typography>
                            : null}
                        <div className={classes.cardGrid}>
                            {renderThemesList()}
                        </div>
                        {theme === 'custom' ? <CustomThemeBuilder
                            updateDisabled={customThemeParams === editCustomThemeParams}
                            editCustomThemeParams={editCustomThemeParams}
                            onUpdate={handleCustomThemeEdited}
                            onUpdateClicked={handleCustomThemeUpdatePressed} /> : <></>}
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">

                        <Typography variant="h6">Background</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                        <div className={classes.cardGrid}>
                            {renderBackgroundList()}
                        </div>
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">

                        <Typography variant="h6">Cards</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                        <CustomCardBuilder
                            customCardColor={customCardColor}
                            setCustomCardColor={setCustomCardColor}
                            useCustomCardColor={useCustomCardColor}
                            setUseCustomCardColor={setUseCustomCardColor}
                            cardOutlineColor={cardOutlineColor}
                            setCardOutlineColor={setCardOutlineColor}
                            useCustomCardOutline={useCustomCardOutline}
                            setUseCustomCardOutline={setUseCustomCardOutline}
                            cardOutlineThickness={cardOutlineThickness}
                            setCardOutlineThickness={setCardOutlineThickness}
                            cardImage={cardImage}
                            setCardImage={setCardImage}
                            customCardImage={customCardImage}
                            setCustomCardImage={setCustomCardImage} />
                    </AccordionDetails>
                </Accordion>
            </div>
        </div>
    )
}
